/**
 * Layer Z-Order System
 *
 * Defines the default render order for layer types.
 * Lower numbers render first (bottom), higher render last (top).
 * POIs always render on top (z-order 100).
 */

import type { ThemeLayerKey, ThemeConfig } from "./themes/types";

/**
 * Default z-order for each layer type.
 * This determines the visual stacking order on the map.
 *
 * Order (low to high):
 * - 0: boundary (fill only, acts as map "canvas")
 * - 1: water
 * - 2: vegetation, landscape, zone
 * - 3: parking, point amenities
 * - 4: path_secondary
 * - 5: path_primary
 * - 6: barriers, rail, bus, tunnel
 * - 7: buildings, custom (topmost non-POI)
 * - 8: bridges (elevated, goes over everything)
 * - 9: vignette (fog-of-war, on top of geometry)
 * POIs render via separate symbol layers on top of all geometry.
 */
export const LAYER_TYPE_Z_ORDER: Record<ThemeLayerKey, number> = {
  boundary: 0,
  water: 1,
  waterway: 1.5,
  vegetation: 2,
  landscape: 2,
  zone: 2,
  parking: 3,
  prop: 7.5, // Above buildings (7), below bridge (8) and vignette (9)
  // Plaza (theme-only key for path_wide Polygon variant). Sits just below
  // path_wide line so plaza fill renders under wide-path strokes when both
  // overlap, matching admin expectations from the Phase C wrap-up.
  plaza: 3.7,
  // Line layers
  path_secondary: 4,
  path_tertiary: 3.5,
  path_wide: 3.8,
  path_primary: 5,
  barrier: 6,
  rail: 6,
  bus: 6,
  tunnel: 6,
  // Top layers
  building: 7,
  building_primary: 7,
  building_secondary: 7,
  building_entrance: 7,
  custom_polygon: 7,
  custom_path: 5.5,
  bridge: 8,
  vignette: 9,
};

/**
 * Default z-order for custom map stickers (plan 120).
 *
 * Stickers don't live in the `layers` table (parallel `map_illustrations`
 * table), so they're not in `LAYER_TYPE_Z_ORDER`. This slot is reserved at
 * 7.7 — above props (7.5), above buildings (7), below bridges (8) and
 * vignette (9). Per-instance `z_order_override` (bounded 0..9 by DB CHECK)
 * lets admins push individual stickers above bridges or below paths.
 */
export const STICKER_DEFAULT_Z_ORDER = 7.7;

/**
 * Get the z-order for a layer type.
 * Checks theme config first, falls back to default constant.
 * Returns 4 for unknown types.
 */
export function getLayerZOrder(layerType: string, theme?: ThemeConfig): number {
  if (theme) {
    const style = theme.layers[layerType as ThemeLayerKey];
    if (style?.zOrder !== undefined) return style.zOrder;
  }
  return LAYER_TYPE_Z_ORDER[layerType as ThemeLayerKey] ?? 4;
}

